import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from "recharts";
import { Button } from "../components/ui/button";
import { Download } from "lucide-react";

const COLORS = ["#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#8b5cf6"];

export default function Reports() {
  const { t } = useI18n();
  const [compliance, setCompliance] = useState([]);
  const [turnover, setTurnover] = useState([]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    (async () => {
      const [c, tt, a] = await Promise.all([
        api.get("/reports/compliance"), api.get("/reports/turnover"), api.get("/alerts/expiring?days=180")
      ]);
      setCompliance(c.data); setTurnover(tt.data); setAlerts(a.data);
    })();
  }, []);

  const compliantCount = compliance.filter(c => c.compliant).length;
  const nonCompliant = compliance.length - compliantCount;
  const rate = compliance.length ? Math.round((compliantCount / compliance.length) * 100) : 100;

  const severityBuckets = ["expired", "critical", "warning", "info"].map(sev => ({
    name: t(sev) || sev,
    count: alerts.filter(a => a.severity === sev).length
  }));

  const exportCsv = () => {
    const header = "Employee,IssuesCount,Compliant,Issues\n";
    const body = compliance.map(c => `"${c.name}",${c.issues_count},${c.compliant},"${c.issues.join(';')}"`).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "compliance_report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 border-border/60">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-mono-brand">{t("complianceRate")}</div>
          <div className="font-display text-4xl font-extrabold mt-2">{rate}%</div>
          <div className="mt-2 text-xs text-muted-foreground">{compliantCount}/{compliance.length} drivers compliant</div>
        </Card>
        <Card className="p-5 border-border/60">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-mono-brand">{t("expiringDocs")}</div>
          <div className="font-display text-4xl font-extrabold mt-2">{alerts.length}</div>
          <div className="mt-2 text-xs text-muted-foreground">next 180 days</div>
        </Card>
        <Card className="p-5 border-border/60">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-mono-brand">{t("turnoverRate")}</div>
          <div className="font-display text-4xl font-extrabold mt-2">{turnover.reduce((s, x) => s + x.count, 0)}</div>
          <div className="mt-2 text-xs text-muted-foreground">cumulative departures</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border-border/60">
          <h3 className="font-display font-bold mb-3">{t("docExpiryDist")}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={severityBuckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {severityBuckets.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-border/60">
          <h3 className="font-display font-bold mb-3">{t("turnoverRate")}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={turnover}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="period" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
              <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-5 border-border/60">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold">{t("complianceRate")} — Drivers</h3>
          <Button data-testid="btn-export-csv" size="sm" variant="outline" onClick={exportCsv}>
            <Download className="w-3 h-3 mr-1" /> {t("export")}
          </Button>
        </div>
        {compliance.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">{t("noData")}</div> : (
          <div className="divide-y divide-border/60">
            {compliance.map((c, i) => (
              <div key={c.employee_id} className="py-2.5 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate font-mono-brand">{c.issues.join(" · ") || "OK"}</div>
                </div>
                <Badge variant={c.compliant ? "default" : "destructive"}>{c.compliant ? "COMPLIANT" : `${c.issues_count} ${c.issues_count === 1 ? "issue" : "issues"}`}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
